/** Phone-sized layout detection and touch gestures (swipe between drawers, long-press). */
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

const MOBILE_QUERY = '(max-width: 767px)';

function subscribe(cb: () => void) {
  const mq = window.matchMedia(MOBILE_QUERY);
  mq.addEventListener('change', cb);
  return () => mq.removeEventListener('change', cb);
}

/** True below Tailwind's `md` breakpoint: one pane at a time, drawers for the rest. */
export function useIsMobile() {
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(MOBILE_QUERY).matches,
    () => false,
  );
}

export const SWIPE_MIN_DISTANCE = 60;
/** Only from this close to the left edge can a right swipe open the nav drawer. */
export const EDGE_WIDTH = 32;

/**
 * Decide what a finished pointer movement was. A swipe must be long enough and mostly
 * horizontal, so scrolling the message list never opens a drawer by accident.
 */
export function classifySwipe(
  start: { x: number; y: number },
  end: { x: number; y: number },
  opts: { edgeOnly?: boolean } = {},
): 'left' | 'right' | null {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (Math.abs(dx) < SWIPE_MIN_DISTANCE || Math.abs(dx) < Math.abs(dy) * 2) return null;
  if (dx > 0) return opts.edgeOnly && start.x > EDGE_WIDTH ? null : 'right';
  return 'left';
}

/**
 * Horizontal swipes on an element (touch and pen only; mice drag-select text). Returns a
 * callback ref, so it works for elements that mount later than the component.
 */
export function useSwipe(
  handlers: { onLeft?: () => void; onRight?: () => void; rightFromEdgeOnly?: boolean },
  enabled = true,
) {
  const latest = useRef(handlers);
  latest.current = handlers;
  return useCallback(
    (el: HTMLElement | null) => {
      if (!el || !enabled) return;
      let start: { x: number; y: number } | null = null;
      const down = (e: PointerEvent) => {
        start = e.pointerType === 'mouse' ? null : { x: e.clientX, y: e.clientY };
      };
      const up = (e: PointerEvent) => {
        if (!start) return;
        const dir = classifySwipe(
          start,
          { x: e.clientX, y: e.clientY },
          { edgeOnly: latest.current.rightFromEdgeOnly },
        );
        start = null;
        if (dir === 'left') latest.current.onLeft?.();
        if (dir === 'right') latest.current.onRight?.();
      };
      const cancel = () => (start = null);
      el.addEventListener('pointerdown', down);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', cancel);
      return () => {
        el.removeEventListener('pointerdown', down);
        el.removeEventListener('pointerup', up);
        el.removeEventListener('pointercancel', cancel);
      };
    },
    [enabled],
  );
}

export const LONG_PRESS_MS = 450;
/** Moving further than this cancels a long-press (the user is scrolling). */
export const LONG_PRESS_SLOP = 10;

/** Props for an element that fires `onLongPress` when held still (touch only). */
export function useLongPress(onLongPress: () => void, enabled = true) {
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  /** The click the browser sends right after lifting the finger isn't a tap. */
  const suppressClickUntil = useRef(0);
  const clear = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  };
  useEffect(() => clear, []);
  if (!enabled) return {};
  return {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      fired.current = false;
      origin.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => {
        fired.current = true;
        clear();
        navigator.vibrate?.(10);
        onLongPress();
      }, LONG_PRESS_MS);
    },
    onPointerMove: (e: React.PointerEvent) => {
      const o = origin.current;
      if (o && Math.hypot(e.clientX - o.x, e.clientY - o.y) > LONG_PRESS_SLOP) clear();
    },
    onPointerUp: () => {
      if (fired.current) suppressClickUntil.current = Date.now() + 400;
      fired.current = false;
      clear();
    },
    onPointerCancel: clear,
    // A long-press shouldn't also open the browser's context menu or "click" what's under it.
    onContextMenu: (e: React.MouseEvent) => {
      if (fired.current || timer.current !== null) e.preventDefault();
    },
    onClickCapture: (e: React.MouseEvent) => {
      // Taps inside what the long-press opened (a dialog) are real.
      const inDialog = (e.target as Element).closest('[role="dialog"]');
      if (Date.now() < suppressClickUntil.current && !inDialog) {
        e.preventDefault();
        e.stopPropagation();
        suppressClickUntil.current = 0;
      }
    },
  };
}
